import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CommonDataService } from '../../../services/common-data.service';
import { CommonFunctionsService } from '../../../services/common-functions.service';
import { CostReviewEstimateType, CULTURE_ID, ProductOffering, ProjectCategoryVerification } from '../../../models/enum';
import { Constants } from '../../../constants/constants';
import { ReviewDetails } from '../../../models/ReviewDetails';
import { Subject, merge, takeUntil } from 'rxjs';
import { ReviewService } from '../../../services/review.service';
import { ExportTitleCaseMap } from '../../../models/PdfRelatedModels';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ExportObjectAsFieldValuePDFOptions } from '../../../models/ExportObjectAsFieldValuePDFOptions';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { SiFormFieldComponent } from '../../Si-Web-Toolkit/si-form-field/si-form-field.component';
import { SiDropDownComponent } from '../../Si-Web-Toolkit/si-drop-down/si-drop-down.component';
import { SiRadioButtonComponent } from "../../Si-Web-Toolkit/si-radio-button/si-radio-button.component"; 
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { SiDatePickerComponent } from '../../Si-Web-Toolkit/si-date-picker/si-date-picker.component';
import { CostReviewSummary } from '../../../models/CostReviewSummary';
import { DecimalPrecisionPipe } from '../../../pipes/decimal-precision/decimal-precision.pipe';
import { ReviewDetailTabsService } from '../../../lib/review-detail-tabs.service';
import { ActionNeededField } from '../../../models/ActionNeededFieldChange';
import { CostReviewDatabaseRecord } from '../../../models/CostReviewDatabaseRecord';
import { TrackChangesDirective } from '../../../Directives/track-changes.directive';
import { createEmptyJobDetailsFields, IJobDetailsFields } from '../../../models/IJobDetailsFields';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-sol-summary-tab',
  imports: [
    CommonModule,
    TranslatePipe,
    MatRadioModule,
    MatIconModule,
    SiFormFieldComponent,
    SiDropDownComponent,
    SiRadioButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    SiDatePickerComponent,
    MatTooltipModule,
    MatIconModule,
    TrackChangesDirective,
    MatButtonModule
],
  providers: [DecimalPrecisionPipe],
  templateUrl: './sol-summary-tab.component.html',
  styleUrl: './sol-summary-tab.component.scss'
})
export class SolSummaryTabComponent implements OnInit, OnDestroy {

    isReadOnlyMode: boolean = false;
    dropDownList =  [
      {label: Constants.PROJECT_CATEGORY_W, value: Constants.PROJECT_CATEGORY_W},
      {label: Constants.PROJECT_CATEGORY_T, value: Constants.PROJECT_CATEGORY_T},
      {label: Constants.PROJECT_CATEGORY_S, value: Constants.PROJECT_CATEGORY_S}
    ]

  costReviewSOLSummaryFormGroup! : FormGroup<{
    mvReporting: FormControl<string>;
    commissioningRequirements: FormControl<string>;
    clarityOfScope: FormControl<string>;
    resourceRequirements: FormControl<string>;
    thirdPartyResources: FormControl<string>;
    siteAccess: FormControl<string>;
    highWorkArea: FormControl<string>;
    projectScope: FormControl<string>;
    sufficientSpace: FormControl<string>;
    construction: FormControl<string>;
    alternatesIncluded: FormControl<string>;
    contigencyIncluded: FormControl<number | string>;
    estimateDepth: FormControl<string>;
    projectEndDate: FormControl<Date | null>;
    projectStartDate: FormControl<Date | null>;
    projectDuration: FormControl<string>;
    projectCategoryVerification: FormControl<string>;
    smApprovalRecieved: FormControl<string>;
    consultingEngineer: FormControl<string>;
    architectureFirm: FormControl<string>;
    constructionManager: FormControl<string>;
    jobsiteRtMileage: FormControl<string>;
    projectScopeDescription: FormControl<string>;
    thirdPartyResourceRequiredDescription: FormControl<string>;
    authorityHavingJurisdiction: FormControl<string>;
    zteApprovalReceived: FormControl<string>;
    nationalOrEnterpriseAccount: FormControl<string>;
    siemensPrimeBidder: FormControl<string>;
  }>;

  isDisabled: boolean = false;
  constants = Constants;
  costReviewSummary: CostReviewSummary | undefined = new CostReviewSummary();
  business: string = '';
  isSalePriceGreaterThan150: boolean = false;
  private isDateSyncInProgress: boolean = false;
  @Output() disablePdf = new EventEmitter<boolean>();
  destroy$ = new Subject<void>();
  projectCategoryVerificationList = [{value: this.constants.PROJECT_CATEGORY_W, label: this.constants.PROJECT_CATEGORY_W}, 
    {value: this.constants.PROJECT_CATEGORY_T, label: this.constants.PROJECT_CATEGORY_T}, {value: this.constants.PROJECT_CATEGORY_S, label: this.constants.PROJECT_CATEGORY_S}];
  jobDetails: IJobDetailsFields = createEmptyJobDetailsFields();
  productOfferingsDisplay: string = Constants.EMPTY_STRING;
  isJobDetailsButtonDisabled: boolean = false;
  constructor(
    private readonly commonDataService: CommonDataService, 
    private readonly commonFunctionsService: CommonFunctionsService, 
    private readonly reviewService: ReviewService, 
    private readonly translate: TranslateService,
    private readonly decimalPrecisionPipe: DecimalPrecisionPipe,
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly reviewDetailTabsService: ReviewDetailTabsService
  ) { }

  ngOnInit(): void {
     const estimateType = this.reviewService.convertToEstimateType(
      this.reviewDetailTabsService.getOptionalWorkflowAndEstimateType().optionalEstimateType
    );
    this.isSalePriceGreaterThan150 = estimateType === CostReviewEstimateType.EstimateGreater150K;
     this.getReviewLibraryState();
    this.createFormGroup();
    this.initializeSummaryData();
    this.subscribeToDataRefresh();
    this.subscribeToDateChanges();
    this.commonDataService.triggerTabMethod$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.exportToPdf(this.reviewService.selectedReview, Constants.SOL_SUMMARY);
      });
    this.sendDisablePdfEvent();
    this.setupReadOnlyMode();
    this.subscribeToReviewFormDataSyncRequest();
    this.subscribeToTaskBasedSettings();    
  }

  /** Allows the host app to force this tab to push its latest data without waiting for tab switch/destroy. */
  private subscribeToReviewFormDataSyncRequest(): void {
    this.reviewDetailTabsService.getReviewFormDataSyncRequested$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.pushFormDataToService());
  }

  private subscribeToTaskBasedSettings(): void {
    this.reviewDetailTabsService.taskBasedLaborSettings$
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings) => {
        this.jobDetails = this.reviewDetailTabsService.mapToJobDetailsFields(settings);
        this.updateProductOfferingsDisplay();
        this.isJobDetailsButtonDisabled = !settings;
      });    
  }

  private updateProductOfferingsDisplay(): void {
    const selectedOfferings: ProductOffering[] = [];

    if (this.jobDetails.fireAlarmOffering) {
      selectedOfferings.push(ProductOffering.FIRE_ALARM);
    }
    if (this.jobDetails.fireSprinklerOffering) {
      selectedOfferings.push(ProductOffering.FIRE_SPRINKLER);
    }
    if (this.jobDetails.gaseousSystemOffering) {
      selectedOfferings.push(ProductOffering.GASEOUS_SYSTEM);
    }
    if (this.jobDetails.ercesOffering) {
      selectedOfferings.push(ProductOffering.ERCES);
    }

    this.productOfferingsDisplay = selectedOfferings
      .map((offering) => this.translate.instant(offering))
      .join(', ');
  }


  private getReviewLibraryState(): void {
    this.isDisabled = this.reviewDetailTabsService.getReviewLibraryDisableFlag();
  }


  private setupReadOnlyMode(): void {
    this.commonDataService.libraryReadOnlyMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((readOnly: boolean) => {
        const isLibraryUsedInSQ = this.reviewDetailTabsService.getIsLibraryUsedInSQ();
        if (isLibraryUsedInSQ) {
          this.isDisabled = readOnly;
          this.isReadOnlyMode = false;
          this.costReviewSOLSummaryFormGroup.enable({ emitEvent: false });
        } else {
          this.isReadOnlyMode = readOnly;
          this.costReviewSOLSummaryFormGroup.disable({ emitEvent: false });
        }
        if (readOnly) {
          this.refreshProjectDatesFromHost();
        }
      });
  }

  /**
   * Re-reads the project dates from the host-provided cost review record, which the app keeps
   * aligned with the estimate. The form is never the source of truth for dates coming in - only
   * for edits made while in write mode.
   */
  private refreshProjectDatesFromHost(): void {
    const summary = this.reviewService.costReviewDetails?.reviewObject?.costReviewSummary;
    if (!summary || !this.costReviewSOLSummaryFormGroup) {
      return;
    }

    // The host may have replaced the record, so re-point at the summary it currently holds.
    this.costReviewSummary = summary;
    this.isDateSyncInProgress = true;
    this.costReviewSOLSummaryFormGroup.patchValue({
      projectStartDate: summary.projectStartDate ? new Date(summary.projectStartDate) : null,
      projectEndDate: summary.projectEndDate ? new Date(summary.projectEndDate) : null,
      projectDuration: summary.projectDuration?.toString() || Constants.EMPTY_STRING
    }, { emitEvent: false });
    this.isDateSyncInProgress = false;
  }

  sendDisablePdfEvent() {
    this.disablePdf.emit(false);
  }

  private subscribeToDataRefresh(): void {
    this.reviewService.onDataRefreshed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const isLibraryUsedInSQ = this.reviewDetailTabsService.getIsLibraryUsedInSQ();
        this.isDisabled = isLibraryUsedInSQ ? this.commonDataService.libraryReadOnlyMode : this.isDisabled;
        this.isReadOnlyMode = isLibraryUsedInSQ ? false : this.commonDataService.libraryReadOnlyMode;
        this.initializeSummaryData();
      });
  }

  /**
   * Read mode means the host (and therefore the estimate) owns the project dates: the form must
   * neither push them back nor overwrite them. The host reports write mode for the `Not Started`
   * and `Denied` cost review states only.
   */
  private isReviewReadOnly(): boolean {
    return this.commonDataService.libraryReadOnlyMode;
  }

  private subscribeToDateChanges(): void {
    const startCtrl = this.costReviewSOLSummaryFormGroup.controls.projectStartDate;
    const endCtrl = this.costReviewSOLSummaryFormGroup.controls.projectEndDate;
    const durCtrl = this.costReviewSOLSummaryFormGroup.controls.projectDuration;

    // When start or end date changes, recalculate duration
    merge(startCtrl.valueChanges, endCtrl.valueChanges)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isDateSyncInProgress || !this.costReviewSummary || this.isReviewReadOnly()) {
          return;
        }
        this.isDateSyncInProgress = true;

        const formData = this.costReviewSOLSummaryFormGroup.getRawValue();
        const startDate = formData.projectStartDate ? new Date(formData.projectStartDate) : null;
        const endDate = formData.projectEndDate ? new Date(formData.projectEndDate) : null;

        if (startDate) {
          this.costReviewSummary.projectStartDate = startDate;
        }
        if (endDate) {
          this.costReviewSummary.projectEndDate = endDate;
        }

        // Validate: if start > end, sync end to start + 1 day
        if (this.costReviewSummary.projectStartDate && this.costReviewSummary.projectEndDate
          && this.isDateBefore(this.costReviewSummary.projectEndDate, this.costReviewSummary.projectStartDate)) {
          const correctedEnd = this.addDays(this.costReviewSummary.projectStartDate, 1);
          this.costReviewSummary.projectEndDate = correctedEnd;
          endCtrl.setValue(correctedEnd, { emitEvent: false });
        }

        // Recalculate duration (minimum 1 day)
        if (this.costReviewSummary.projectStartDate && this.costReviewSummary.projectEndDate) {
          const duration = Math.max(1, this.calculateDurationDays(this.costReviewSummary.projectStartDate, this.costReviewSummary.projectEndDate));
          this.costReviewSummary.projectDuration = duration;
          durCtrl.setValue(duration.toString(), { emitEvent: false });
        }

        this.isDateSyncInProgress = false;
        this.emitDateChange();
      });

    // When duration changes manually, recalculate end date
    durCtrl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isDateSyncInProgress || !this.costReviewSummary || this.isReviewReadOnly()) return;
        this.isDateSyncInProgress = true;

        // Minimum 1 day duration
        const durationVal = Math.max(1, Number(durCtrl.value) || 1);
        this.costReviewSummary.projectDuration = durationVal;

        if (durationVal !== Number(durCtrl.value)) {
          durCtrl.setValue(durationVal.toString(), { emitEvent: false });
        }

        if (this.costReviewSummary.projectStartDate) {
          const newEnd = this.addDays(this.costReviewSummary.projectStartDate, durationVal);
          this.costReviewSummary.projectEndDate = newEnd;
          endCtrl.setValue(newEnd, { emitEvent: false });
        }

        this.isDateSyncInProgress = false;
        this.emitDateChange();
      });
  }

  private emitDateChange(): void {
    this.reviewDetailTabsService.notifyProjectDateChanged({
      projectStartDate: this.costReviewSummary?.projectStartDate ?? null,
      projectEndDate: this.costReviewSummary?.projectEndDate ?? null,
      projectDuration: this.costReviewSummary?.projectDuration ?? 0
    });
  }

  private calculateDurationDays(startDate: Date, endDate: Date): number {
    const dayInMs = 24 * 60 * 60 * 1000;
    const start = new Date(startDate).setHours(0, 0, 0, 0);
    const end = new Date(endDate).setHours(0, 0, 0, 0);
    return Math.max(0, Math.round((end - start) / dayInMs));
  }

  private addDays(baseDate: Date, days: number): Date {
    const result = new Date(baseDate);
    result.setDate(result.getDate() + days);
    return result;
  }

  private isDateBefore(leftDate: Date, rightDate: Date): boolean {
    const left = new Date(leftDate).setHours(0, 0, 0, 0);
    const right = new Date(rightDate).setHours(0, 0, 0, 0);
    return left < right;
  }

   createFormGroup() {
    this.costReviewSOLSummaryFormGroup = this.formBuilder.group({
      mvReporting: this.formBuilder.control(Constants.EMPTY_STRING),
      commissioningRequirements: this.formBuilder.control(Constants.EMPTY_STRING),
      clarityOfScope: this.formBuilder.control(Constants.EMPTY_STRING),
      resourceRequirements: this.formBuilder.control(Constants.EMPTY_STRING),
      thirdPartyResources: this.formBuilder.control(Constants.EMPTY_STRING),
      siteAccess: this.formBuilder.control(Constants.EMPTY_STRING),
      highWorkArea: this.formBuilder.control(Constants.EMPTY_STRING),
      projectScope: this.formBuilder.control(Constants.EMPTY_STRING),
      sufficientSpace: this.formBuilder.control(Constants.EMPTY_STRING),
      construction: this.formBuilder.control(Constants.EMPTY_STRING),
      alternatesIncluded: this.formBuilder.control(Constants.EMPTY_STRING),
      contigencyIncluded: this.formBuilder.control<number | string>(0),
      siemensPrimeBidder: this.formBuilder.control(Constants.EMPTY_STRING),
      estimateDepth: this.formBuilder.control(Constants.EMPTY_STRING),
      projectEndDate: this.formBuilder.control<Date | null>(null),
      projectStartDate: this.formBuilder.control<Date | null>(null),
      projectDuration: this.formBuilder.control(Constants.EMPTY_STRING),
      projectCategoryVerification: this.formBuilder.control(Constants.EMPTY_STRING),
      smApprovalRecieved: this.formBuilder.control(Constants.EMPTY_STRING),
      consultingEngineer: this.formBuilder.control(Constants.EMPTY_STRING),
      architectureFirm: this.formBuilder.control(Constants.EMPTY_STRING),
      constructionManager: this.formBuilder.control(Constants.EMPTY_STRING),
      jobsiteRtMileage: this.formBuilder.control(Constants.EMPTY_STRING),
      projectScopeDescription: this.formBuilder.control(Constants.EMPTY_STRING),
      thirdPartyResourceRequiredDescription: this.formBuilder.control(Constants.EMPTY_STRING),
      authorityHavingJurisdiction: this.formBuilder.control(Constants.EMPTY_STRING),
      zteApprovalReceived: this.formBuilder.control(Constants.EMPTY_STRING),
      nationalOrEnterpriseAccount: this.formBuilder.control(Constants.EMPTY_STRING)
    });
  }

  ngOnDestroy() {
    this.pushFormDataToService();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private pushFormDataToService(): void {
    if (!this.costReviewSOLSummaryFormGroup || !this.costReviewSummary) return;
    const formData = this.costReviewSOLSummaryFormGroup.getRawValue();

    this.costReviewSummary.mvReporting = this.getParameterCode(this.constants.YES_NO, formData.mvReporting, this.costReviewSummary.mvReporting);
    this.costReviewSummary.commissioningRequirements = this.getParameterCode(this.constants.COMMISSIONING_REQUIREMENTS, formData.commissioningRequirements, this.costReviewSummary.commissioningRequirements);
    this.costReviewSummary.clarityOfScope = this.getParameterCode(this.constants.CLARITY_OF_SCOPE, formData.clarityOfScope, this.costReviewSummary.clarityOfScope);
    this.costReviewSummary.resourceRequirements = this.getParameterCode(this.constants.RESOURCE_REQUIREMENTS, formData.resourceRequirements, this.costReviewSummary.resourceRequirements);
    this.costReviewSummary.thirdPartyResources = this.getParameterCode(this.constants.YES_NO_UNKNOWN, formData.thirdPartyResources, this.costReviewSummary.thirdPartyResources);
    this.costReviewSummary.siteAccess = this.getParameterCode(this.constants.YES_NO_UNKNOWN, formData.siteAccess, this.costReviewSummary.siteAccess);
    this.costReviewSummary.highWorkArea = this.getParameterCode(this.constants.YES_NO, formData.highWorkArea, this.costReviewSummary.highWorkArea);
    this.costReviewSummary.projectScope = this.getParameterCode(this.constants.PROJECT_SCOPE, formData.projectScope, this.costReviewSummary.projectScope);
    this.costReviewSummary.sufficientSpace = this.getParameterCode(this.constants.YES_NO, formData.sufficientSpace, this.costReviewSummary.sufficientSpace);
    this.costReviewSummary.construction = this.getParameterCode(this.constants.CONSTRUCTION, formData.construction, this.costReviewSummary.construction);
    this.costReviewSummary.alternatesIncluded = this.getParameterCode(this.constants.YES_NO, formData.alternatesIncluded, this.costReviewSummary.alternatesIncluded);
    this.costReviewSummary.siemensPrimeBidder = this.getParameterCode(this.constants.YES_NO, formData.siemensPrimeBidder, this.costReviewSummary.siemensPrimeBidder);
    this.costReviewSummary.estimateDepth = this.getParameterCode(this.constants.ESTIMATE_DEPTH, formData.estimateDepth, this.costReviewSummary.estimateDepth);
    this.costReviewSummary.smApprovalRecieved = this.getParameterCode(this.constants.YES_NO, formData.smApprovalRecieved, this.costReviewSummary.smApprovalRecieved);
    this.costReviewSummary.zteApprovalReceived = this.getParameterCode(this.constants.YES_NO, formData.zteApprovalReceived, this.costReviewSummary.zteApprovalReceived);

    // Direct fields - dates are only written back in write mode, since in read mode the app owns
    // them and the form may still hold the values it was showing before the dates changed there.
    if (!this.isReviewReadOnly()) {
      if (this.business === this.constants.BUSINESS_SECURITY) {
        this.costReviewSummary.contigencyIncluded = Number(formData.contigencyIncluded);
      }
      if (formData.projectStartDate) this.costReviewSummary.projectStartDate = new Date(formData.projectStartDate);
      if (formData.projectEndDate) this.costReviewSummary.projectEndDate = new Date(formData.projectEndDate);
      if (formData.projectDuration) this.costReviewSummary.projectDuration = this.commonFunctionsService.toNumberOrCurrent(formData.projectDuration, this.costReviewSummary.projectDuration);
    }

    // Project category verification (label → code)
    const pcvMap: Record<string, number> = {
      [this.constants.PROJECT_CATEGORY_W]: 0,
      [this.constants.PROJECT_CATEGORY_T]: 1,
      [this.constants.PROJECT_CATEGORY_S]: 2
    };
    if (formData.projectCategoryVerification in pcvMap) {
      this.costReviewSummary.projectCategoryVerification = pcvMap[formData.projectCategoryVerification];
      this.costReviewSummary.isUserProjectCategory = true;
    }

    this.costReviewSummary.consultingEngineer = formData.consultingEngineer;
    this.costReviewSummary.architectureFirm = formData.architectureFirm;
    this.costReviewSummary.constructionManager = formData.constructionManager;
    this.costReviewSummary.jobsiteRtMileage = Number(formData.jobsiteRtMileage) ?? 0;
    this.costReviewSummary.projectScopeDescription = formData.projectScopeDescription;
    this.costReviewSummary.thirdPartyResourceRequiredDescription = formData.thirdPartyResourceRequiredDescription;
    this.costReviewSummary.nationalOrEnterpriseAccount = this.getParameterCode(this.constants.YES_NO, formData.nationalOrEnterpriseAccount, this.costReviewSummary.nationalOrEnterpriseAccount);
  }

  private getParameterCode(parameter: string, description: string, currentValue: number | null): number {
    const code = this.reviewService.getCostReviewParametersCode(parameter, description);
    return code !== undefined ? code : (currentValue ?? 0);
  }

  initializeSummaryData() {
    this.isSalePriceGreaterThan150 = this.reviewDetailTabsService.estimateSellPrice > this.constants.ONE_LAKH_FIFTY_THOUSAND;
    let data: CostReviewDatabaseRecord = this.reviewService.costReviewDetails;
    this.business = this.reviewService.getBusiness();
    this.costReviewSummary = data.reviewObject?.costReviewSummary;
    this.isDateSyncInProgress = true;
    this.mapSummaryDataToForm();
    this.isDateSyncInProgress = false;
  }

  mapSummaryDataToForm() {
    if (this.costReviewSummary === undefined || this.costReviewSummary === null) {
      return; 
    }

    this.costReviewSOLSummaryFormGroup.patchValue({
      mvReporting: this.getParameterValues(this.constants.YES_NO, this.costReviewSummary.mvReporting),
      commissioningRequirements: this.getParameterValues(this.constants.COMMISSIONING_REQUIREMENTS, this.costReviewSummary.commissioningRequirements),
      clarityOfScope: this.getParameterValues(this.constants.CLARITY_OF_SCOPE, this.costReviewSummary.clarityOfScope),
      resourceRequirements: this.getParameterValues(this.constants.RESOURCE_REQUIREMENTS, this.costReviewSummary.resourceRequirements),
      thirdPartyResources: this.getParameterValues(this.constants.YES_NO_UNKNOWN, this.costReviewSummary.thirdPartyResources),
      siteAccess: this.getParameterValues(this.constants.YES_NO_UNKNOWN, this.costReviewSummary.siteAccess),
      highWorkArea: this.getParameterValues(this.constants.YES_NO, this.costReviewSummary.highWorkArea),
      projectScope: this.getParameterValues(this.constants.PROJECT_SCOPE, this.costReviewSummary.projectScope),
      sufficientSpace: this.getParameterValues(this.constants.YES_NO, this.costReviewSummary.sufficientSpace),
      construction: this.getParameterValues(this.constants.CONSTRUCTION, this.costReviewSummary.construction),
      alternatesIncluded: this.getParameterValues(this.constants.YES_NO, this.costReviewSummary.alternatesIncluded),
      contigencyIncluded: this.costReviewSummary.contigencyIncluded ?? 0,
      siemensPrimeBidder: this.getParameterValues(this.constants.YES_NO, this.costReviewSummary.siemensPrimeBidder),
      estimateDepth: this.getParameterValues(this.constants.ESTIMATE_DEPTH, this.costReviewSummary.estimateDepth),
      projectEndDate: this.costReviewSummary.projectEndDate ? new Date(this.costReviewSummary.projectEndDate) : null,
      projectStartDate: this.costReviewSummary.projectStartDate ? new Date(this.costReviewSummary.projectStartDate) : null,
      projectDuration: this.costReviewSummary.projectDuration?.toString() || Constants.EMPTY_STRING,
      projectCategoryVerification: this.getProjectCategoryVerificationValue(this.costReviewSummary.projectCategoryVerification),
      smApprovalRecieved: this.getParameterValues(this.constants.YES_NO, this.costReviewSummary?.smApprovalRecieved?.toString()),
      consultingEngineer: this.costReviewSummary.consultingEngineer ?? Constants.EMPTY_STRING,
      architectureFirm: this.costReviewSummary.architectureFirm ?? Constants.EMPTY_STRING,
      constructionManager: this.costReviewSummary.constructionManager ?? Constants.EMPTY_STRING,
      jobsiteRtMileage: this.costReviewSummary.jobsiteRtMileage?.toString() ?? Constants.EMPTY_STRING,
      projectScopeDescription: this.costReviewSummary.projectScopeDescription ?? Constants.EMPTY_STRING,
      thirdPartyResourceRequiredDescription: this.costReviewSummary.thirdPartyResourceRequiredDescription ?? Constants.EMPTY_STRING,
      authorityHavingJurisdiction: this.costReviewSummary.authorityHavingJurisdiction ?? Constants.EMPTY_STRING,
      zteApprovalReceived: this.getParameterValues(this.constants.YES_NO, this.costReviewSummary.zteApprovalReceived),
      nationalOrEnterpriseAccount: this.getParameterValues(this.constants.YES_NO, this.costReviewSummary.nationalOrEnterpriseAccount)
    
    });
  }
  
  getParameterValues(key: string, value: string | number | undefined): string {
    return this.reviewService.getCostReviewParametersDescription(key, Number(value));
  }

  private getCultureIdFromPreferences(): number {
    const locale = this.reviewDetailTabsService.getUserCultureString();

    if (locale === this.constants.LANGUAGE_FR_CA) {
      return CULTURE_ID.FR_CA;
    }

    if (locale === this.constants.LANGUAGE_EN_CA) {
      return CULTURE_ID.EN_CA;
    }

    return CULTURE_ID.EN_US;
  }

  private getEstimateSubsegmentId(): number | undefined {
    const subsegmentId = this.reviewDetailTabsService.getSubsegmentId();
    return subsegmentId === null ? undefined : subsegmentId;
  }

  private getServiceProjectSubsegmentIdsByCulture(cultureId: number): readonly number[] {
    if (cultureId === CULTURE_ID.EN_US) {
      return [
        this.constants.AUTOMATION_SERVICE_PROJECT_US,
        this.constants.MECHANICAL_SERVICE_PROJECT_US,
      ];
    }

    return [
      this.constants.AUTOMATION_SERVICE_PROJECT_CN_EN,
      this.constants.AUTOMATION_SERVICE_PROJECT_CN_FR,
      this.constants.MECHANICAL_SERVICE_PROJECT_CN_EN,
      this.constants.MECHANICAL_SERVICE_PROJECT_CN_FR,
    ];
  }

  isServiceProjectSubsegment(): boolean {
    const resolvedSubsegmentId = this.getEstimateSubsegmentId();
    if (!resolvedSubsegmentId) {
      return false;
    }

    const cultureId = this.getCultureIdFromPreferences();
    return this.getServiceProjectSubsegmentIdsByCulture(cultureId).includes(resolvedSubsegmentId);
  }

  getProjectCategoryVerificationValue(value: string | undefined | number): string {
    switch (value?.toString()) {
      case ProjectCategoryVerification.ProjectCategory0:
        return this.constants.PROJECT_CATEGORY_W;

      case ProjectCategoryVerification.ProjectCategory1:
        return this.constants.PROJECT_CATEGORY_T;

      case ProjectCategoryVerification.ProjectCategory2:
        return this.constants.PROJECT_CATEGORY_S;

      default:
        return '';
    }
  }

  exportToPdf(selectedReview: ReviewDetails, tabName: string) {
    if (tabName === Constants.SOL_SUMMARY) {

      if (
        !this.costReviewSummary ||
        Object.keys(this.costReviewSummary).length === Constants.ZERO
      ) {
        const options: ExportObjectAsFieldValuePDFOptions = {
         data: { 'Summary Data not found': Constants.EMPTY_STRING },
          title: Constants.EMPTY_STRING,
          fileName: 'summary.pdf',
          estimateID: selectedReview.estimateID,
          estimateName: selectedReview.request,
          tabName: this.translate.instant('REVIEW_SOL_TABS_LABELS.SUMMARY'),
        };
        this.commonFunctionsService.exportObjectAsFieldValuePDF(options);
        return;
      }

      const pdfData: ExportTitleCaseMap = {};

      this.addAlwaysFields(pdfData);
      this.addFireFields(pdfData);
      this.addConsultingEngineerField(pdfData);
      this.addArchitectureFirmField(pdfData);
      this.addConstructionManagerField(pdfData);
      this.addJobsiteMileageField(pdfData);
      this.addProjectDatesFields(pdfData);
      this.addEstimateDepthField(pdfData);
      this.addCollaborativeField(pdfData);
      this.addTaskBasedEstimatingFields(pdfData);
      this.addAlternativesIncludedField(pdfData);
      this.addOpportunityLinkedField(pdfData);
      this.addConstructionField(pdfData);
      this.addSufficientSpaceField(pdfData);
      this.addGraphicsStandardField(pdfData);
      this.addVerticalMarketField(pdfData);
      this.addLaborEfficiencyField(pdfData);
      this.addProjectScopeFields(pdfData);
      this.addClarityOfScopeField(pdfData);
      this.addCommissioningRequirementsField(pdfData);
      this.addMVReportingField(pdfData);
      this.addResourceRequirementsField(pdfData);
      this.addThirdPartyResourcesFields(pdfData);
      this.addSiteAccessField(pdfData);
      this.addHighWorkAreaField(pdfData);
      this.addZteApprovalField(pdfData);
      this.addSecuritySpecificFields(pdfData);
      this.addMarginRecommenderField(pdfData);
      this.addCDPField(pdfData);
      this.addEnergyOMField(pdfData);

      const options: ExportObjectAsFieldValuePDFOptions = {
        data: pdfData,
        title: this.translate.instant('REVIEW_SOL_SUMMARY_TAB.TITLE'),
        fileName: 'summary.pdf',
        estimateID: selectedReview.estimateID,
        estimateName: selectedReview.request,
        tabName: this.translate.instant('REVIEW_SOL_TABS_LABELS.SUMMARY'),
      };
      this.commonFunctionsService.exportObjectAsFieldValuePDF(options);
    }
  }

  addAlwaysFields(pdfData: ExportTitleCaseMap) {
    pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.PROJECT_NAME')] = this.costReviewSummary?.projectName ?? Constants.EMPTY_STRING;
    pdfData[this.translate.instant('COMMON_LABELS.ACCOUNT_NAME')] = this.costReviewSummary?.customerName ?? Constants.EMPTY_STRING;
    pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.SHIP_TO_ADDRESS')] = this.costReviewSummary?.shipToLocation ?? Constants.EMPTY_STRING;
    pdfData['Project Category Verification'] = this.getProjectCategoryVerificationValue(this.costReviewSummary?.projectCategoryVerification);
  }

  addFireFields(pdfData: ExportTitleCaseMap) {
    if (this.business === this.constants.BUSINESS_FIRE) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.AUTHORITY_HAVING_JURISDICTION')] = this.costReviewSummary?.authorityHavingJurisdiction ?? Constants.EMPTY_STRING;
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.NATIONAL_OR_ENTERPRISE_ACCOUNT')] = this.getParameterValues(this.constants.YES_NO, this.costReviewSummary?.nationalOrEnterpriseAccount);
    }
  }

  addConsultingEngineerField(pdfData: ExportTitleCaseMap) {
    if (
      (this.business !== this.constants.BUSINESS_SECURITY) &&
      (
        this.business === this.constants.BUSINESS_AUTOMATION ||
        this.business === this.constants.BUSINESS_MECHANICAL ||
        (this.business === this.constants.BUSINESS_FIRE && this.isSalePriceGreaterThan150)
      )
    ) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.CONSULTING_ENGINEER')] = this.costReviewSummary?.consultingEngineer ?? Constants.EMPTY_STRING;
    }
  }

  addArchitectureFirmField(pdfData: ExportTitleCaseMap) {
    if (
      this.business === this.constants.BUSINESS_SECURITY ||
      (
        (this.business === this.constants.BUSINESS_FIRE ||
          this.business === this.constants.BUSINESS_AUTOMATION||
          this.business === this.constants.BUSINESS_MECHANICAL) &&
        this.isSalePriceGreaterThan150
      )
    ) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.ARCHITECTURE_ENGINEERING_FIRM')] = this.costReviewSummary?.architectureFirm ?? Constants.EMPTY_STRING;
    }
  }

  addConstructionManagerField(pdfData: ExportTitleCaseMap) {
    if (this.isSalePriceGreaterThan150) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.CONSTRUCTION_MANAGER')] = this.costReviewSummary?.constructionManager ?? Constants.EMPTY_STRING;
    }
  }

  addJobsiteMileageField(pdfData: ExportTitleCaseMap) {
    if (
      this.business === this.constants.BUSINESS_SECURITY ||
      (
        (this.business === this.constants.BUSINESS_AUTOMATION ||
          this.business === this.constants.BUSINESS_MECHANICAL) &&
        this.isSalePriceGreaterThan150
      )
    ) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.JOBSITE_RT_MILEAGE')] = this.costReviewSummary?.jobsiteRtMileage?.toString() ?? Constants.EMPTY_STRING;
    }
  }

  addProjectDatesFields(pdfData: ExportTitleCaseMap) {
    pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.PROJECT_START_DATE')] = this.costReviewSummary?.projectStartDate
      ? new Date(this.costReviewSummary.projectStartDate).toLocaleDateString(this.commonDataService.selectedLocale)
      : Constants.EMPTY_STRING;
    pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.DURATION_DAYS')] = this.costReviewSummary?.projectDuration?.toString() ?? Constants.EMPTY_STRING;
    pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.PROJECT_END_DATE')] = this.costReviewSummary?.projectEndDate
      ? new Date(this.costReviewSummary.projectEndDate).toLocaleDateString(this.commonDataService.selectedLocale)
      : Constants.EMPTY_STRING;
  }

  addEstimateDepthField(pdfData: ExportTitleCaseMap) {
    if (this.business !== this.constants.BUSINESS_SECURITY) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.ESTIMATE_DEPTH')] = this.getParameterValues(this.constants.ESTIMATE_DEPTH, this.costReviewSummary?.estimateDepth);
    }
  }

  addCollaborativeField(pdfData: ExportTitleCaseMap) {
    pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.COLLABORATIVE')] = this.costReviewSummary?.isCollaborative ?? Constants.EMPTY_STRING;
  }

  addTaskBasedEstimatingFields(pdfData: ExportTitleCaseMap) {
    if (this.costReviewSummary?.isFireTBEVisibility && this.costReviewSummary?.fireTaskBasedEstimating) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.TASK_BASED_ESTIMATING_USED')] = this.costReviewSummary?.fireTaskBasedEstimating;
    }
    if (
      this.costReviewSummary?.isFireTBEVisibility &&
      this.costReviewSummary?.fireTaskBasedEstimating &&
      !this.isFireTaskBasedEstimatingYes
    ) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.SM_APPROVAL_RECEIVED')] = this.getParameterValues(this.constants.YES_NO, this.costReviewSummary?.smApprovalRecieved?.toString());
    }
  }

  addAlternativesIncludedField(pdfData: ExportTitleCaseMap) {
    if (this.business !== this.constants.BUSINESS_SECURITY) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.ALTERNATIVES_INCLUDED')] = this.getParameterValues(this.constants.YES_NO, this.costReviewSummary?.alternatesIncluded);
    }
  }

  addSiemensPrimeBidderField(pdfData: ExportTitleCaseMap) {
    if (this.business !== this.constants.BUSINESS_SECURITY) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.SIEMENS_PRIME_BIDDER')] = this.getParameterValues(this.constants.YES_NO, this.costReviewSummary?.siemensPrimeBidder);
    }
  }

  addOpportunityLinkedField(pdfData: ExportTitleCaseMap) {
    if (this.business !== this.constants.BUSINESS_SECURITY) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.OPPORTUNITY_LINKED')] = this.costReviewSummary?.opportunityLinked ?? Constants.EMPTY_STRING;
    }
  }

  addConstructionField(pdfData: ExportTitleCaseMap) {
    if (this.business !== this.constants.BUSINESS_SECURITY) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.CONSTRUCTION')] = this.getParameterValues(this.constants.CONSTRUCTION, this.costReviewSummary?.construction);
    }
  }

  addSufficientSpaceField(pdfData: ExportTitleCaseMap) {
    if (
      this.business !== this.constants.BUSINESS_SECURITY &&
      this.costReviewSummary?.construction &&
      this.costReviewSummary?.construction == 1
    ) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.IF_NEW_SUFFICIENT_SPACE')] = this.getParameterValues(this.constants.YES_NO, this.costReviewSummary?.sufficientSpace);
    }
  }

  addGraphicsStandardField(pdfData: ExportTitleCaseMap) {
    if (this.costReviewSummary?.isGraphicsStandardVerifiedWithGISVisible && this.business !== this.constants.BUSINESS_SECURITY) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.GRAPHICS_STANDARD_VERIFIED')] = this.getParameterValues(this.constants.YES_NO, this.costReviewSummary?.graphicsStandardVerifiedWithGIS);
    }
  }

  addVerticalMarketField(pdfData: ExportTitleCaseMap) {
    if (this.costReviewSummary?.verticalMarket) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.VERTICAL_MARKET')] = this.costReviewSummary?.verticalMarket;
    }
  }

  addLaborEfficiencyField(pdfData: ExportTitleCaseMap) {
    if (this.isLaborEfficiencyVisible) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.LABOR_EFFICIENCY')] = this.costReviewSummary?.laborEfficiency;
    }
  }

  addProjectScopeFields(pdfData: ExportTitleCaseMap) {
    if (this.business !== this.constants.BUSINESS_SECURITY) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.PROJECT_SCOPE')] = this.getParameterValues(this.constants.PROJECT_SCOPE, this.costReviewSummary?.projectScope);
      if (this.costReviewSummary?.projectScopeDescription) {
        pdfData['Project scope description'] = this.costReviewSummary?.projectScopeDescription;
      }
    }
  }

  addClarityOfScopeField(pdfData: ExportTitleCaseMap) {
    if (this.business !== this.constants.BUSINESS_SECURITY) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.CLARITY_OF_SCOPE')] = this.getParameterValues(this.constants.CLARITY_OF_SCOPE, this.costReviewSummary?.clarityOfScope);
    }
  }

  addCommissioningRequirementsField(pdfData: ExportTitleCaseMap) {
    if (this.business !== this.constants.BUSINESS_SECURITY) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.COMMISSIONING_REQUIREMENTS')] = this.getParameterValues(this.constants.COMMISSIONING_REQUIREMENTS, this.costReviewSummary?.commissioningRequirements);
    }
  }

  addMVReportingField(pdfData: ExportTitleCaseMap) {
    if (this.business !== this.constants.BUSINESS_SECURITY && this.business !== this.constants.BUSINESS_FIRE) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.M_AND_V_REPORTING_REQUIRED')] = this.getParameterValues(this.constants.YES_NO, this.costReviewSummary?.mvReporting);
    }
  }

  addResourceRequirementsField(pdfData: ExportTitleCaseMap) {
    if (this.business !== this.constants.BUSINESS_SECURITY) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.RESOURCE_REQUIREMENTS')] = this.getParameterValues(this.constants.RESOURCE_REQUIREMENTS, this.costReviewSummary?.resourceRequirements);
    }
  }

  addThirdPartyResourcesFields(pdfData: ExportTitleCaseMap) {
    if (this.business !== this.constants.BUSINESS_SECURITY) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.THIRD_PARTY_RESOURCES_REQUIRED')] = this.getParameterValues(this.constants.YES_NO_UNKNOWN, this.costReviewSummary?.thirdPartyResources);
      if (this.costReviewSummary?.thirdPartyResourceRequiredDescription) {
        pdfData['3rd Party resource required description'] = this.costReviewSummary?.thirdPartyResourceRequiredDescription;
      }
    }
  }

  addSiteAccessField(pdfData: ExportTitleCaseMap) {
    if (this.business !== this.constants.BUSINESS_SECURITY) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.SITE_ACCESS')] = this.getParameterValues(this.constants.YES_NO_UNKNOWN, this.costReviewSummary?.siteAccess);
    }
  }

  addHighWorkAreaField(pdfData: ExportTitleCaseMap) {
    if (this.business !== this.constants.BUSINESS_SECURITY) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.HIGH_WORK_AREA')] = this.getParameterValues(this.constants.YES_NO, this.costReviewSummary?.highWorkArea);
    }
  }

  addZteApprovalField(pdfData: ExportTitleCaseMap) {
    if (this.business === this.constants.BUSINESS_FIRE) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.ZTE_APPROVAL_RECEIVED')] = this.getParameterValues(this.constants.YES_NO, this.costReviewSummary?.zteApprovalReceived);
    }
  }

  addSecuritySpecificFields(pdfData: ExportTitleCaseMap) {
    if (this.business === this.constants.BUSINESS_SECURITY) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.ESTIMATE_DEPTH')] = this.getParameterValues(this.constants.ESTIMATE_DEPTH, this.costReviewSummary?.estimateDepth);
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.OPPORTUNITY_LINKED')] = this.costReviewSummary?.opportunityLinked ?? Constants.EMPTY_STRING;
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.PROPOSAL_LINKED')] = this.costReviewSummary?.proposalLinked ?? Constants.EMPTY_STRING;
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.ALTERNATES_INCLUDED')] = this.getParameterValues(this.constants.YES_NO, this.costReviewSummary?.alternatesIncluded);
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.SIEMENS_PRIME_BIDDER')] = this.getParameterValues(this.constants.YES_NO, this.costReviewSummary?.siemensPrimeBidder);
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.CONTINGENCY_INCLUDED')] = this.costReviewSummary?.contigencyIncluded != null
        ? `${this.decimalPrecisionPipe.transform(this.costReviewSummary?.contigencyIncluded)}%` : Constants.EMPTY_STRING;
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.SIEMENS_A_PRIME_BIDDER')] = this.getParameterValues(this.constants.YES_NO, this.costReviewSummary?.siemensPrimeBidder);
    }
  }

  addMarginRecommenderField(pdfData: ExportTitleCaseMap) {
    if (
      this.business !== this.constants.BUSINESS_AUTOMATION &&
      this.business !== this.constants.BUSINESS_MECHANICAL &&
      this.costReviewSummary?.isPriceRecommenderUsed
    ) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.MARGIN_RECOMMENDER_USED')] = this.costReviewSummary?.isPriceRecommenderUsed;
    }
  }

  addCDPField(pdfData: ExportTitleCaseMap) {
    if (this.costReviewSummary?.isCDP) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.CONNECTED_DIGITAL_PACKAGE')] = this.costReviewSummary?.isCDP;
    }
  }

  addEnergyOMField(pdfData: ExportTitleCaseMap) {
    if (this.costReviewSummary?.isEnergyOMReviewRequired) {
      pdfData[this.translate.instant('REVIEW_SOL_SUMMARY_TAB.ENERGY_OM_COST_REVIEW_REQUIRED')] = this.costReviewSummary?.isEnergyOMReviewRequired;
    }
  }

  onSmApprovalChange() {
    if (!this.costReviewSummary) {
      return;
    }
    const newValue = (this.costReviewSOLSummaryFormGroup.get('smApprovalRecieved')?.value) ?? '';
    const numericValue = this.commonFunctionsService.isValueEqualToTranslation(newValue, 'YES') ? 1 : 2;
    this.costReviewSummary.smApprovalRecieved = numericValue;
    this.reviewService.notifyFieldChange(ActionNeededField.FireSmApprovalReceived, numericValue);
  }

  get projectCategoryVerification(): string {
    return this.costReviewSOLSummaryFormGroup.get('projectCategoryVerification')?.value ?? Constants.EMPTY_STRING;
  }

  get estimateDepth(): string {
    return this.costReviewSOLSummaryFormGroup.get('estimateDepth')?.value ?? Constants.EMPTY_STRING;
  }

  get alternatesIncluded(): string {
    return this.costReviewSOLSummaryFormGroup.get('alternatesIncluded')?.value ?? Constants.EMPTY_STRING;
  }

  get siemensPrimeBidder(): string {
    return this.costReviewSOLSummaryFormGroup.get('siemensPrimeBidder')?.value ?? Constants.EMPTY_STRING;
  }

  get construction(): string {
    return this.costReviewSOLSummaryFormGroup.get('construction')?.value ?? Constants.EMPTY_STRING;
  }

  get sufficientSpace(): string {
    return this.costReviewSOLSummaryFormGroup.get('sufficientSpace')?.value ?? Constants.EMPTY_STRING;
  }

  get projectScope(): string {
    return this.costReviewSOLSummaryFormGroup.get('projectScope')?.value ?? Constants.EMPTY_STRING;
  }

  get clarityOfScope(): string {
    return this.costReviewSOLSummaryFormGroup.get('clarityOfScope')?.value ?? Constants.EMPTY_STRING;
  }

  get commissioningRequirements(): string {
    return this.costReviewSOLSummaryFormGroup.get('commissioningRequirements')?.value ?? Constants.EMPTY_STRING;
  }

  get mvReporting(): string {
    return this.costReviewSOLSummaryFormGroup.get('mvReporting')?.value ?? Constants.EMPTY_STRING;
  }

  get resourceRequirements(): string {
    return this.costReviewSOLSummaryFormGroup.get('resourceRequirements')?.value ?? Constants.EMPTY_STRING;
  }

  get thirdPartyResources(): string {
    return this.costReviewSOLSummaryFormGroup.get('thirdPartyResources')?.value ?? Constants.EMPTY_STRING;
  }

  get siteAccess(): string {
    return this.costReviewSOLSummaryFormGroup.get('siteAccess')?.value ?? Constants.EMPTY_STRING;
  }

  get highWorkArea(): string {
    return this.costReviewSOLSummaryFormGroup.get('highWorkArea')?.value ?? Constants.EMPTY_STRING;
  }

  get smApprovalRecieved(): string {
    return this.costReviewSOLSummaryFormGroup.get('smApprovalRecieved')?.value ?? Constants.EMPTY_STRING;
  }

  get isAutomationOrElectrical(): boolean {
    return this.business === Constants.BUSINESS_AUTOMATION || this.business === Constants.BUSINESS_ELECTRICAL || 
    this.business === Constants.BUSINESS_FIRE;
  }

  get isLaborEfficiencyVisible(): boolean {
    return this.business === Constants.BUSINESS_AUTOMATION && !!this.costReviewSummary?.laborEfficiency;
  }

  get isAutomationFireSecurity(): boolean {
    return ((this.business === Constants.BUSINESS_AUTOMATION || 
      this.business === Constants.BUSINESS_MECHANICAL ||
      this.business === Constants.BUSINESS_ELECTRICAL ||
      this.business === Constants.BUSINESS_FIRE) && this.isSalePriceGreaterThan150) || 
      (this.business === Constants.BUSINESS_SECURITY);
  }

  get isSufficientSpaceDisabled(): boolean {
    return this.commonFunctionsService.isValueEqualToTranslation(this.construction, 'COST_REVIEW_SUMMARY_TAB_REHAB');
  }

  get isProjectScopeDescDisabled(): boolean {
    return this.projectScope === this.translate.instant('COST_REVIEW_SUMMARY_TAB_PROVEN_TECHNOLOGY');
  }

  get isThirdPartyResourceDescDisabled(): boolean {
    return this.thirdPartyResources !== this.translate.instant('YES');
  }

  get isFireTaskBasedEstimatingYes(): boolean {
    const value = this.costReviewSummary?.fireTaskBasedEstimating;
    // Backend string: accept the English constant as well as the current-culture translation.
    return value === this.constants.YES || this.commonFunctionsService.isValueEqualToTranslation(value, 'YES');
  }

  get jobDetailsInstallationType(): string {
    return this.jobDetails?.installationType || this.reviewDetailTabsService.getJobDetailsProductTypes()[0] || Constants.EMPTY_STRING;
  }

  openJobDetailsDialog() {
    const taskBasedLaborSettings = this.reviewDetailTabsService.getTaskBasedLaborSettings();
    if (!taskBasedLaborSettings) {
      return;
    }
    this.reviewDetailTabsService.requestJobDetailsDialog();
  }
}
